import React, { useCallback, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowRight, Check, Layers } from 'lucide-react';
import { digitalCardStyles } from '../digitalcard/digitalCardStyles';
import { fieldTeamStyles } from './fieldTeamStyles';
import FieldTeamNav from './FieldTeamNav';
import FieldTeamFooter from './FieldTeamFooter';
import FieldTeamLoginCard from './FieldTeamLoginCard';
import { HeroDevicePair } from './DeviceMockups';
import { getFieldTeamSolution } from './fieldTeamSolutions';
import type { FieldTeamAuthState } from './fieldTeamAuth';
import {
  CONTACT_PATH,
  DEMO_PATH,
  DOWNLOAD_PATH,
  LOGIN_SECTION_ID,
  SOLUTION_PATHS,
  SOLUTIONS_SECTION_ID,
  fieldTeamSectionUrl,
  type FieldTeamSolutionSlug,
} from './fieldTeamRoutes';

type Props = FieldTeamAuthState & {
  slug: FieldTeamSolutionSlug;
};

export const FieldTeamSolutionPage: React.FC<Props> = ({ slug, ...auth }) => {
  const { t } = useTranslation();
  const solution = getFieldTeamSolution(slug);
  const Icon = solution.icon;
  const openSignup = useCallback(() => auth.setSignupOpen(true), [auth]);
  const points = [1, 2, 3, 4].map((n) => t(`ftl_sol_${slug}_p${n}`));

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  return (
    <div className="dcl-root dcl-root--linktree ftl-root">
      <style>{digitalCardStyles}</style>
      <style>{fieldTeamStyles}</style>
      <FieldTeamNav activePath={SOLUTION_PATHS[slug]} onGetStarted={openSignup} />

      <section className="dcl-hero-lt">
        <div className="dcl-container dcl-hero-lt-inner">
          <div className="dcl-hero-lt-copy ftl-sol-hero-copy">
            <div className="dcl-section-badge">
              <Icon size={14} />
              {t(`ftl_sol_${slug}_nav`)}
            </div>
            <h1 className="dcl-hero-lt-title">{t(`ftl_sol_${slug}_hero`)}</h1>
            <p className="dcl-hero-lt-sub">{t(`ftl_sol_${slug}_sub`)}</p>
            <div className="dcl-hero-lt-actions" style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
              <button type="button" className="dcl-btn dcl-btn--primary dcl-btn--pill" onClick={openSignup}>
                {t('ftl_cta_signup_free')}
                <ArrowRight size={18} />
              </button>
              <Link to={DEMO_PATH} className="dcl-btn dcl-btn--secondary dcl-btn--pill">
                {t('ftl_cta_demo')}
              </Link>
              <Link to={fieldTeamSectionUrl(SOLUTIONS_SECTION_ID)} className="dcl-btn dcl-btn--ghost dcl-btn--pill">
                {t('ftl_sol_back')}
              </Link>
            </div>
            <p className="dcl-signin-link dcl-signin-link--hero ftl-hero-text-links">
              <a href={`#${LOGIN_SECTION_ID}`}>{t('ftl_cta_signin')}</a>
              <span aria-hidden="true"> · </span>
              <Link to={CONTACT_PATH}>{t('ftl_nav_contact')}</Link>
              <span aria-hidden="true"> · </span>
              <a href={DOWNLOAD_PATH}>{t('ftl_nav_download')}</a>
            </p>
          </div>
          <div className="dcl-hero-lt-visual">
            <HeroDevicePair
              laptopSrc={solution.laptopSrc}
              laptopAlt={t(solution.laptopAltKey)}
              phoneSrc={solution.phoneSrc}
              phoneAlt={t(solution.phoneAltKey)}
            />
          </div>
        </div>
      </section>

      <section className="dcl-section dcl-section--white dcl-section--mesh">
        <div className="dcl-container">
          <div className="dcl-section-header">
            <div className="dcl-section-badge">
              <Layers size={14} />
              {t('ftl_sol_fit_badge', { defaultValue: 'How TCards fits' })}
            </div>
            <h2 className="dcl-section-title dcl-section-title--center">{t(`ftl_sol_${slug}_fit_title`)}</h2>
          </div>
          <ul className="ftl-feature-list" style={{ maxWidth: 720, margin: '0 auto' }}>
            {points.map((point) => (
              <li key={point}>
                <span className="dcl-pricing-feature-icon"><Check size={12} /></span>
                <span>{point}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      <section className={`dcl-section dcl-section--soft dcl-section--mesh ftl-scroll-target`} id={LOGIN_SECTION_ID}>
        <div className="dcl-container ftl-login-grid">
          <div>
            <h2 className="dcl-section-title">{t('ftl_login_title')}</h2>
            <p className="dcl-section-sub" style={{ marginTop: 12 }}>{t('ftl_sol_login_sub')}</p>
          </div>
          <FieldTeamLoginCard
            email={auth.email}
            password={auth.password}
            showPassword={auth.showPassword}
            loading={auth.loading}
            storeLinks={auth.storeLinks}
            onEmailChange={auth.onEmailChange}
            onPasswordChange={auth.onPasswordChange}
            onTogglePassword={auth.onTogglePassword}
            onSubmit={auth.onSubmit}
            onOpenSignup={openSignup}
          />
        </div>
      </section>

      <section className="dcl-section dcl-final-lt">
        <div className="dcl-container dcl-container--narrow">
          <h2 className="dcl-final-lt-title">{t(`ftl_sol_${slug}_final`)}</h2>
          <p className="dcl-final-lt-sub">{t('ftl_final_sub')}</p>
          <div className="ftl-final-actions">
            <button type="button" className="dcl-btn dcl-btn--primary dcl-btn--pill dcl-btn--lg" onClick={openSignup}>
              {t('ftl_cta_signup_free')}
              <ArrowRight size={18} />
            </button>
            <Link to={DEMO_PATH} className="dcl-btn dcl-btn--secondary dcl-btn--pill dcl-btn--lg">
              {t('ftl_cta_demo')}
            </Link>
          </div>
        </div>
      </section>

      <FieldTeamFooter onGetStarted={openSignup} />
    </div>
  );
};

export default FieldTeamSolutionPage;
